import { Link } from "react-router-dom";

/**
 * Placeholder Privacy Policy. Wording is drafted from the operational reality
 * of AMDS but LEGAL NAME / ADDRESS / ICO REG are left as `[[…]]` placeholders
 * to be filled in by the owner before formal launch.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-2xl mx-auto px-5 py-10 prose-amds space-y-6">
        <div>
          <div className="label-overline">Legal</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2 text-ink-900">Privacy Policy</h1>
          <p className="text-ink-500 text-sm mt-2">Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>

        <div className="rounded-2xl bg-amber-soft border border-amber-deep/30 p-4 text-sm text-ink-800">
          <strong>Placeholder version.</strong> This document is a placeholder legal draft
          based on how AMDS actually operates. The trading name, registered address and
          data-protection contact will be filled in before formal launch. The Trust
          summary at{" "}<Link to="/privacy-security" className="underline text-sage-600">Privacy &amp; Security</Link>{" "}
          explains the same content in plain English.
        </div>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">1. Who we are</h2>
        <p>
          AMDS (&quot;All Musicians Diary Service&quot;) is operated by
          <strong> [[YOUR BUSINESS NAME]]</strong>, registered at
          <strong> [[YOUR REGISTERED ADDRESS]]</strong>. For any privacy-related enquiry,
          contact <strong>[[privacy@amdsdiary.org]]</strong>.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">2. What we collect</h2>
        <p>
          When you use AMDS we collect only the data required to run the service:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Account: name, email, role (musician / booker / conductor / admin), password (hashed with bcrypt, never stored in plain text).</li>
          <li>Profile: instrument(s), voice type, location, travel-radius preference, playing standard, ensemble experience, short bio, optional photo.</li>
          <li>Diary: events you create manually, events created from confirmed bookings, and events you optionally import from a .ics file.</li>
          <li>Interactions: bookings, dep responses, messages between users of the platform.</li>
          <li>Operational: IP address, browser user-agent, timestamps on key actions (login, verify, password reset) — used for auditing and abuse prevention only.</li>
        </ul>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">3. How we use it</h2>
        <p>
          Data is used exclusively to operate AMDS features: finding musicians and deps,
          checking availability, sending booking / dep notifications, and running the diary.
          We do not sell your data. We do not use it to train external AI models.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">4. Diary privacy</h2>
        <p>
          The contents of your diary — event titles, venues, notes, exact start/end times —
          are visible only to you when signed in. When a booker searches, they see only
          whether you are available, tentative or unavailable for the requested time band.
          Event details are never shared.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">5. Third parties</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Resend</strong> — transactional email delivery (verification, password reset, booking notifications, feedback receipts).</li>
          <li><strong>Stripe</strong> — payment processing if you upgrade to a paid tier. AMDS never sees your full card number.</li>
          <li><strong>MongoDB Atlas</strong> — cloud database hosting.</li>
        </ul>
        <p>
          Each processor is bound by its own privacy policy and by a data-processing
          agreement with AMDS.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">6. Your rights (UK GDPR)</h2>
        <p>
          You have the right to access, correct, export and erase your data. You can update
          your profile at any time from the Profile page, delete your account from
          Profile → Settings, or write to
          <strong> [[privacy@amdsdiary.org]]</strong> for anything more complex. AMDS
          responds to data-subject requests within one calendar month.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">7. Retention</h2>
        <p>
          Account data is retained for as long as your account is active, plus 30 days
          after deletion to allow for accidental-deletion recovery. Audit logs of login /
          verification / password events are retained for 12 months for security purposes.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">8. Cookies</h2>
        <p>
          AMDS uses a small number of first-party cookies that are strictly necessary to
          keep you signed in (JWT access token + refresh token). We do not use analytics
          cookies, advertising cookies, or third-party tracking.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-ink-900 mt-8">9. Governing law</h2>
        <p>
          This policy is governed by the laws of <strong>[[England &amp; Wales]]</strong>.
          Complaints may also be lodged with the UK Information Commissioner&apos;s Office (ICO).
        </p>

        <p className="text-center text-ink-500 text-xs mt-10">
          <Link to="/" className="hover:text-sage-600">Back to AMDS</Link>
        </p>
      </div>
    </div>
  );
}
